import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AssemblyCategoryResponse,
  AssemblyDocumentResponse,
  AssemblyDayLogInput,
  AssemblyDayWorkerInput,
  AssemblyReportItemInput,
  AssemblyReportResponse,
  AssemblyTaskResponse,
  AssemblyTaskStatus,
  CompleteMaintenanceInput,
  CreateAssemblyTaskInput,
  CreateMaintenanceScheduleInput,
  ItemResponse,
  ListResponse,
  MaintenanceScheduleResponse,
  SendAssemblyDayReportEmailResponse,
  SignAssemblyDayInput,
  TechnicianOption,
  UpdateAssemblyDayLogInput,
  UpdateAssemblyDocumentInput,
  UpdateAssemblyDayWorkerInput,
  UpdateAssemblyReportInput,
  UpdateAssemblyReportItemInput,
  UpdateAssemblyTaskInput,
  UpdateMaintenanceScheduleInput,
} from '@proerp/shared';
import { apiRequest, postAuthedFile } from '@/lib/apiClient';

const TASKS_KEY = ['assembly-tasks'] as const;
const MAINTENANCE_KEY = ['maintenance-schedules'] as const;
const REPORT_KEY = ['assembly-report'] as const;

// ---- Baustellenbericht -----------------------------------------------------

/** Bericht eines Einsatzes (leer, solange nichts erfasst wurde). */
export function useAssemblyReport(assemblyTaskId: string | undefined) {
  return useQuery({
    queryKey: [...REPORT_KEY, assemblyTaskId],
    queryFn: () => apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report`),
    enabled: Boolean(assemblyTaskId),
  });
}

/**
 * Einzelne Felder speichern (Auto-Save). Die Antwort ersetzt den Cache direkt —
 * kein Nachladen, damit auf der Baustelle nichts flackert.
 */
export function useUpdateAssemblyReport(assemblyTaskId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateAssemblyReportInput) =>
      apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: (result) => queryClient.setQueryData([...REPORT_KEY, assemblyTaskId], result),
  });
}

/**
 * Baustellenverlauf: Tag anlegen/ändern/entfernen. Alle drei Endpunkte liefern
 * den ganzen Bericht zurück (die Summen ändern sich mit jedem Tag), deshalb
 * genügt hier dasselbe Cache-Ersetzen wie beim Kopf — kein Nachladen.
 */
function useDayMutation<TVars>(
  assemblyTaskId: string | undefined,
  request: (vars: TVars) => Promise<ItemResponse<AssemblyReportResponse>>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (result) => queryClient.setQueryData([...REPORT_KEY, assemblyTaskId], result),
  });
}

export function useAddAssemblyDay(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, (input: AssemblyDayLogInput) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/days`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateAssemblyDay(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ dayId, input }: { dayId: string; input: UpdateAssemblyDayLogInput }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/days/${dayId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  );
}

export function useRemoveAssemblyDay(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, (dayId: string) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/days/${dayId}`, {
      method: 'DELETE',
    }),
  );
}

// ---- Bau-Tagesbericht: Personal & Unterschriften (ADR 0074) ----------------

const dayPath = (taskId: string | undefined, dayId: string) => `/v1/assembly-tasks/${taskId}/report/days/${dayId}`;

export function useAddDayWorker(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ dayId, input }: { dayId: string; input: AssemblyDayWorkerInput }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`${dayPath(assemblyTaskId, dayId)}/workers`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateDayWorker(assemblyTaskId: string | undefined) {
  return useDayMutation(
    assemblyTaskId,
    ({ dayId, workerId, input }: { dayId: string; workerId: string; input: UpdateAssemblyDayWorkerInput }) =>
      apiRequest<ItemResponse<AssemblyReportResponse>>(`${dayPath(assemblyTaskId, dayId)}/workers/${workerId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
  );
}

export function useRemoveDayWorker(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ dayId, workerId }: { dayId: string; workerId: string }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`${dayPath(assemblyTaskId, dayId)}/workers/${workerId}`, {
      method: 'DELETE',
    }),
  );
}

export function useSignAssemblyDay(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ dayId, input }: { dayId: string; input: SignAssemblyDayInput }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`${dayPath(assemblyTaskId, dayId)}/signatures`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

/**
 * Bau-Tagesbericht per E-Mail senden (ADR 0076). Ändert den Bericht selbst
 * nicht (nur die hinterlegte Adresse tut das, über `useUpdateAssemblyReport`)
 * — deshalb kein Cache-Ersetzen wie bei den übrigen Bau-Tagesbericht-Mutationen.
 */
export function useSendDayReportEmail(assemblyTaskId: string | undefined) {
  return useMutation({
    mutationFn: (dayId: string) =>
      apiRequest<ItemResponse<SendAssemblyDayReportEmailResponse>>(`${dayPath(assemblyTaskId, dayId)}/email`, {
        method: 'POST',
      }),
  });
}

// ---- Listen & Fotos (Blöcke I/J, ADR 0070 Schritt 5) ----------------------

export function useAddReportItem(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, (input: AssemblyReportItemInput) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/items`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateReportItem(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ itemId, input }: { itemId: string; input: UpdateAssemblyReportItemInput }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  );
}

export function useRemoveReportItem(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, (itemId: string) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/items/${itemId}`, {
      method: 'DELETE',
    }),
  );
}

/** Foto hochladen — Rohdaten im Körper, Metadaten als Query (Muster Picking). */
export function useUploadReportPhoto(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ file, reportItemId, caption }: { file: File; reportItemId?: string | null; caption?: string | null }) => {
    const params = new URLSearchParams({ fileName: file.name });
    if (reportItemId) params.set('reportItemId', reportItemId);
    if (caption) params.set('caption', caption);
    return postAuthedFile<ItemResponse<AssemblyReportResponse>>(
      `/v1/assembly-tasks/${assemblyTaskId}/report/photos?${params.toString()}`,
      file,
    );
  });
}

export function useUpdateReportPhoto(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ photoId, caption }: { photoId: string; caption: string | null }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/photos/${photoId}`, {
      method: 'PATCH',
      body: JSON.stringify({ caption }),
    }),
  );
}

export function useRemoveReportPhoto(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, (photoId: string) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/photos/${photoId}`, {
      method: 'DELETE',
    }),
  );
}

export function useWithdrawDaySignature(assemblyTaskId: string | undefined) {
  return useDayMutation(assemblyTaskId, ({ dayId, role }: { dayId: string; role: string }) =>
    apiRequest<ItemResponse<AssemblyReportResponse>>(`${dayPath(assemblyTaskId, dayId)}/signatures/${role}`, {
      method: 'DELETE',
    }),
  );
}

/** Bericht abschließen bzw. zur Korrektur wieder öffnen. */
function useReportAction(assemblyTaskId: string | undefined, action: 'finalize' | 'reopen') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<AssemblyReportResponse>>(`/v1/assembly-tasks/${assemblyTaskId}/report/${action}`, {
        method: 'POST',
      }),
    onSuccess: (result) => queryClient.setQueryData([...REPORT_KEY, assemblyTaskId], result),
  });
}

export const useFinalizeAssemblyReport = (assemblyTaskId: string | undefined) =>
  useReportAction(assemblyTaskId, 'finalize');
export const useReopenAssemblyReport = (assemblyTaskId: string | undefined) =>
  useReportAction(assemblyTaskId, 'reopen');

// ---- Montage-Einsätze ------------------------------------------------------

export interface AssemblyTasksQuery {
  q?: string;
  projectId?: string;
  status?: AssemblyTaskStatus | '';
  categoryId?: string;
  employeeId?: string;
  /** Nur die Einsätze des angemeldeten Users (serverseitig aufgelöst, ADR 0062). */
  mine?: boolean;
}

export function useAssemblyTasks(query: AssemblyTasksQuery = {}) {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.projectId) params.set('projectId', query.projectId);
  if (query.status) params.set('status', query.status);
  if (query.categoryId) params.set('categoryId', query.categoryId);
  if (query.employeeId) params.set('employeeId', query.employeeId);
  if (query.mine) params.set('mine', 'true');
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return useQuery({
    queryKey: [...TASKS_KEY, query],
    queryFn: () => apiRequest<ListResponse<AssemblyTaskResponse>>(`/v1/assembly-tasks${suffix}`),
  });
}

/** Montage-Kategorien (Pflege: Daten-Editor) für Picker/Filter/Timeline. */
export function useAssemblyCategories() {
  return useQuery({
    queryKey: ['assembly-categories'],
    queryFn: () => apiRequest<ListResponse<AssemblyCategoryResponse>>('/v1/assembly-categories'),
    staleTime: 5 * 60_000,
  });
}

/** Monteur-Optionen (aktive Mitarbeiter der Rolle „Montage") für die Einsatz-Planung. */
export function useTechnicianOptions() {
  return useQuery({
    queryKey: ['assembly-technicians'],
    queryFn: () => apiRequest<ListResponse<TechnicianOption>>('/v1/assembly-technicians'),
    staleTime: 5 * 60_000,
  });
}

/** Kalender-Abo-Token (wird beim ersten Abruf serverseitig erzeugt). */
export function useCalendarToken(enabled: boolean) {
  return useQuery({
    queryKey: ['assembly-calendar-token'],
    queryFn: () => apiRequest<ItemResponse<{ token: string }>>('/v1/assembly-calendar/token'),
    enabled,
  });
}

export function useRotateCalendarToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<{ token: string }>>('/v1/assembly-calendar/token/rotate', {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['assembly-calendar-token'] }),
  });
}

function useTaskMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TASKS_KEY }),
  });
}

export function useCreateAssemblyTask() {
  return useTaskMutation((input: CreateAssemblyTaskInput) =>
    apiRequest<ItemResponse<AssemblyTaskResponse>>('/v1/assembly-tasks', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateAssemblyTask() {
  return useTaskMutation(({ id, input }: { id: string; input: UpdateAssemblyTaskInput }) =>
    apiRequest<ItemResponse<AssemblyTaskResponse>>(`/v1/assembly-tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  );
}

export function useDeleteAssemblyTask() {
  return useTaskMutation((id: string) =>
    apiRequest<void>(`/v1/assembly-tasks/${id}`, { method: 'DELETE' }),
  );
}

// ---- Wartungspläne ---------------------------------------------------------

export function useMaintenanceSchedules(q?: string) {
  const params = q ? `?q=${encodeURIComponent(q)}` : '';
  return useQuery({
    queryKey: [...MAINTENANCE_KEY, { q }],
    queryFn: () =>
      apiRequest<ListResponse<MaintenanceScheduleResponse>>(`/v1/maintenance-schedules${params}`),
  });
}

function useMaintenanceMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MAINTENANCE_KEY }),
  });
}

export function useCreateMaintenance() {
  return useMaintenanceMutation((input: CreateMaintenanceScheduleInput) =>
    apiRequest<ItemResponse<MaintenanceScheduleResponse>>('/v1/maintenance-schedules', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  );
}

export function useUpdateMaintenance() {
  return useMaintenanceMutation(
    ({ id, input }: { id: string; input: UpdateMaintenanceScheduleInput }) =>
      apiRequest<ItemResponse<MaintenanceScheduleResponse>>(`/v1/maintenance-schedules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
  );
}

/** „Wartung durchgeführt" — schreibt die Fälligkeit um das Intervall fort. */
export function useCompleteMaintenance() {
  return useMaintenanceMutation(
    ({ id, input }: { id: string; input?: CompleteMaintenanceInput }) =>
      apiRequest<ItemResponse<MaintenanceScheduleResponse>>(
        `/v1/maintenance-schedules/${id}/complete`,
        { method: 'POST', body: JSON.stringify(input ?? {}) },
      ),
  );
}

export function useDeleteMaintenance() {
  return useMaintenanceMutation((id: string) =>
    apiRequest<void>(`/v1/maintenance-schedules/${id}`, { method: 'DELETE' }),
  );
}

// ---- Dateien am Einsatz (ADR 0087) -----------------------------------------

const DOCUMENTS_KEY = ['assembly-documents'] as const;

/** Unterlagen und Nachweise eines Montage-Einsatzes. */
export function useAssemblyDocuments(assemblyTaskId: string | undefined) {
  return useQuery({
    queryKey: [...DOCUMENTS_KEY, assemblyTaskId],
    queryFn: () =>
      apiRequest<ListResponse<AssemblyDocumentResponse>>(
        `/v1/assembly-tasks/${assemblyTaskId}/documents`,
      ).then((response) => response.data),
    enabled: Boolean(assemblyTaskId),
  });
}

/**
 * Datei ablegen. Der Dateiname wandert als Query-Parameter mit — der Body
 * trägt die Rohbytes, nicht `multipart`, wie bei den übrigen Uploads.
 */
export function useUploadAssemblyDocument(assemblyTaskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, isPartnerVisible }: { file: File; isPartnerVisible: boolean }) =>
      postAuthedFile<ItemResponse<AssemblyDocumentResponse>>(
        `/v1/assembly-tasks/${assemblyTaskId}/documents?fileName=${encodeURIComponent(file.name)}` +
          `&isPartnerVisible=${isPartnerVisible ? 'true' : 'false'}`,
        file,
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...DOCUMENTS_KEY, assemblyTaskId] }),
  });
}

export function useUpdateAssemblyDocument(assemblyTaskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, input }: { documentId: string; input: UpdateAssemblyDocumentInput }) =>
      apiRequest<ItemResponse<AssemblyDocumentResponse>>(
        `/v1/assembly-tasks/${assemblyTaskId}/documents/${documentId}`,
        { method: 'PATCH', body: JSON.stringify(input) },
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...DOCUMENTS_KEY, assemblyTaskId] }),
  });
}

export function useDeleteAssemblyDocument(assemblyTaskId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) =>
      apiRequest(`/v1/assembly-tasks/${assemblyTaskId}/documents/${documentId}`, { method: 'DELETE' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...DOCUMENTS_KEY, assemblyTaskId] }),
  });
}
